import React, { useState } from "react";
import * as styles from "@styles/Refine.css";
import { Send } from "lucide-react";

import type { DocumentRefineViewProps } from "@ps-bridge/contracts/documentRefine";
import type { Section as SectionType } from "@ps-bridge/models/core/dataContracts";
import * as IO from "@ps-bridge/interop/io";
import { ok } from "@ps-bridge/ok";
import { renderMarkdownHtml } from "@utils/markdown";
import { readOptionString } from "@utils/readOptionString";
import { useDocumentAssistantDictation } from "@hooks/useDocumentAssistantDictation";
import DocumentAssistantDictationControl from "@view/document/DocumentAssistantDictationControl";

export type RefineContentViewProps = Pick<DocumentRefineViewProps, "onSubmit"> & {
  section: SectionType;
};

const RefineContentView: React.FC<RefineContentViewProps> = ({ section, onSubmit }) => {
  const [feedback, setFeedbackImpure] = useState("");
  const dictation = useDocumentAssistantDictation({
    fieldId: "refine",
    value: feedback,
    setValue: (value) => {
      setFeedbackImpure(value);
      return ok;
    },
  });
  const sectionHtml = renderMarkdownHtml(readOptionString(section.section_contents));

  return (
    <div className={styles.container}>
      <p className={styles.header}>AI Refine</p>
      <p className={styles.subheader}>{section.metadata.name}:</p>
      <div className={styles.sectionContainer}>
        <div
          data-testid="refine-section-markdown"
          className={styles.section}
          dangerouslySetInnerHTML={{ __html: sectionHtml }}
        />
      </div>
      <div className={styles.inputRow}>
        <textarea
          ref={dictation.inputBindings.ref}
          className={styles.input}
          data-testid="refine-feedback-input"
          value={feedback}
          onChange={(event) => setFeedbackImpure(event.target.value)}
          onClick={dictation.inputBindings.onClick}
          onFocus={dictation.inputBindings.onFocus}
          onSelect={dictation.inputBindings.onSelect}
          placeholder="Enter your feedback here"
          rows={4}
        />
        <DocumentAssistantDictationControl
          {...dictation.controlState}
          buttonClassName={styles.button}
          iconClassName={styles.buttonIcon}
        />
        <button
          className={styles.button}
          data-testid="refine-submit"
          onClick={() => {
            IO.run(onSubmit(feedback));
          }}
        >
          <Send size={16} className={styles.buttonIcon} />
        </button>
      </div>
    </div>
  );
};

export default RefineContentView;
